/**
 * ExtractionCard — Per-extraction review card
 *
 * Renders a single extraction with:
 * - Quoted text (blockquote) from source document
 * - Page number and section heading
 * - Extracted value (rendered by value_type, NOT domain-specific branching)
 * - EDI context (nullable — only shown if present)
 * - Target config path
 * - Confidence badge
 * - Verify / Reject action buttons
 *
 * CRITICAL: No domain-specific switch/if. `value_type` drives rendering.
 * `edi_context` is nullable — non-EDI extractions omit it entirely.
 */

import React, { useState } from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Badge,
  Blockquote,
  Code,
  Button,
  Textarea,
  Modal,
  List,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconFileCode,
  IconMapPin,
} from '@tabler/icons-react';
import { ConfidenceBadge } from './ConfidenceBadge';

// Types matching the GraphQL schema
interface SourceCoordinates {
  page: number | null;
  sectionHeading: string | null;
  quotedText: string;
}

interface EdiContext {
  segment: string | null;
  loop: string | null;
  element: string | null;
  qualifier: string | null;
}

interface ExtractedValue {
  field: string;
  value: string;
  valueType: string | null;
  ediContext: EdiContext | null;
}

interface TargetMapping {
  layer: string;
  sourceFile: string;
  sourcePath: string;
  sourceLine: number | null;
}

export interface Extraction {
  id: string;
  source: SourceCoordinates;
  extracted: ExtractedValue;
  target: TargetMapping;
  confidence: number;
  annotationColor: string | null;
  verificationStatus: string;
  verifiedBy: string | null;
  correctionNote: string | null;
}

interface ExtractionCardProps {
  extraction: Extraction;
  onVerify: (extractionId: string, status: 'CONFIRMED' | 'CORRECTED', note?: string) => void;
  onReject: (extractionId: string, reason: string) => void;
  disabled?: boolean;
}

/** Renders an extracted value based on its value_type — NO domain branching */
function ExtractedValueRenderer({ extracted }: { extracted: ExtractedValue }) {
  const { value, valueType } = extracted;

  // Parse JSON value string
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    parsed = value;
  }

  switch (valueType) {
    case 'boolean':
      return (
        <Badge color={parsed === true ? 'green' : 'gray'} variant="filled">
          {String(parsed)}
        </Badge>
      );

    case 'conditional_rule':
    case 'segment_constraint':
      return <Code block>{typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)}</Code>;

    case 'list':
      if (Array.isArray(parsed)) {
        return (
          <List size="sm">
            {parsed.map((item, i) => (
              <List.Item key={i}>{String(item)}</List.Item>
            ))}
          </List>
        );
      }
      return <Text size="sm">{String(parsed)}</Text>;

    case 'integer':
    case 'float':
      return <Text size="sm" fw={600}>{String(parsed)}</Text>;

    case 'string':
    case 'date':
    case 'enum':
    default:
      return <Text size="sm">{String(parsed)}</Text>;
  }
}

export function ExtractionCard({
  extraction,
  onVerify,
  onReject,
  disabled = false,
}: ExtractionCardProps) {
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const isReviewed = extraction.verificationStatus !== 'UNVERIFIED';
  const statusColor =
    extraction.verificationStatus === 'CONFIRMED' || extraction.verificationStatus === 'CORRECTED'
      ? 'green'
      : extraction.verificationStatus === 'REJECTED'
        ? 'red'
        : 'gray';

  return (
    <>
      <Card withBorder padding="md" radius="sm">
        <Stack gap="sm">
          {/* Header: ID + Confidence + Status */}
          <Group justify="space-between">
            <Group gap="xs">
              <Badge variant="outline" size="sm">
                {extraction.id}
              </Badge>
              {extraction.source.page && (
                <Badge variant="light" size="sm" leftSection={<IconMapPin size={12} />}>
                  Page {extraction.source.page}
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              <ConfidenceBadge
                confidence={extraction.confidence}
                annotationColor={extraction.annotationColor}
              />
              <Badge color={statusColor} size="sm">
                {extraction.verificationStatus}
              </Badge>
            </Group>
          </Group>

          {/* Section heading */}
          {extraction.source.sectionHeading && (
            <Text size="xs" c="dimmed">
              {extraction.source.sectionHeading}
            </Text>
          )}

          {/* Quoted text from source */}
          <Blockquote color="blue" cite="Source Document" icon={null} p="xs">
            <Text size="sm" style={{ fontStyle: 'italic' }}>
              {extraction.source.quotedText}
            </Text>
          </Blockquote>

          {/* Extracted value */}
          <Stack gap={4}>
            <Text size="xs" fw={600} c="dimmed">
              Extracted: {extraction.extracted.field}
              {extraction.extracted.valueType && (
                <Badge size="xs" variant="light" ml="xs">
                  {extraction.extracted.valueType}
                </Badge>
              )}
            </Text>
            <ExtractedValueRenderer extracted={extraction.extracted} />
          </Stack>

          {/* EDI context — only rendered if present (nullable) */}
          {extraction.extracted.ediContext && (
            <Group gap="xs">
              <Text size="xs" c="dimmed">EDI:</Text>
              {extraction.extracted.ediContext.segment && (
                <Badge size="xs" variant="outline">
                  {extraction.extracted.ediContext.segment}
                </Badge>
              )}
              {extraction.extracted.ediContext.loop && (
                <Badge size="xs" variant="outline">
                  Loop {extraction.extracted.ediContext.loop}
                </Badge>
              )}
              {extraction.extracted.ediContext.element && (
                <Badge size="xs" variant="outline">
                  Element {extraction.extracted.ediContext.element}
                </Badge>
              )}
            </Group>
          )}

          {/* Target config path */}
          <Group gap="xs">
            <IconFileCode size={14} />
            <Text size="xs" c="dimmed">
              {extraction.target.layer} &rarr;{' '}
              <Code>{extraction.target.sourceFile}:{extraction.target.sourceLine ?? '?'}</Code>
            </Text>
          </Group>

          {/* Correction note (if exists) */}
          {extraction.correctionNote && (
            <Text size="xs" c="orange">
              Note: {extraction.correctionNote}
            </Text>
          )}

          {/* Action buttons */}
          {!isReviewed && !disabled && (
            <Group gap="xs">
              <Button
                size="xs"
                color="green"
                leftSection={<IconCheck size={14} />}
                onClick={() => onVerify(extraction.id, 'CONFIRMED')}
              >
                Verify
              </Button>
              <Button
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconX size={14} />}
                onClick={() => setRejectModalOpen(true)}
              >
                Reject
              </Button>
            </Group>
          )}
        </Stack>
      </Card>

      {/* Reject modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Extraction"
        size="sm"
      >
        <Stack gap="sm">
          <Text size="sm">
            Rejecting extraction <strong>{extraction.id}</strong> for field{' '}
            <strong>{extraction.extracted.field}</strong>
          </Text>
          <Textarea
            label="Reason for rejection"
            placeholder="Explain what is incorrect..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              disabled={!rejectReason.trim()}
              onClick={() => {
                onReject(extraction.id, rejectReason.trim());
                setRejectModalOpen(false);
                setRejectReason('');
              }}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
